import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { adminTelegramAPI, branchesAPI, departmentsAPI } from '@/api';
import { Card, CardContent, Button, Input, Badge } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination, EmptyState } from '@/components/common';
import { extractPaginatedData } from '@/utils/pagination';
import { Send, Trash2, MessageCircle, Search, RefreshCw } from 'lucide-react';
import BroadcastModal from '@/components/Telegram/BroadcastModal';

type Stats = {
  total_links: number;
  verified_links: number;
  pending_links: number;
  total_active_employees: number;
  linked_employees: number;
  unlinked_employees: number;
  active_last_24h: number;
};

type LinkRow = {
  id: number;
  telegram_id: string | null;
  telegram_user_id?: string | number | null;
  telegram_username: string | null;
  verified_at: string | null;
  last_activity_at: string | null;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
    roles?: { name: string }[];
  } | null;
  employee?: {
    id: number;
    full_name: string;
    employee_id: string | null;
    branch?: { id: number; name: string } | null;
    department?: { id: number; name: string } | null;
  } | null;
};

const formatDateTime = (val: string | null) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return val;
  }
};

export default function TelegramLinksPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [filters, setFilters] = useState({
    q: '',
    branch_id: '' as number | '',
    department_id: '' as number | '',
    role: '',
    verified: '' as boolean | '',
  });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['admin-telegram-stats'],
    queryFn: () => adminTelegramAPI.stats(),
  });
  const stats: Stats | undefined = statsData?.data?.data ?? statsData?.data;

  const { data: listData, isLoading, refetch } = useQuery({
    queryKey: ['admin-telegram-links', filters, page, perPage],
    queryFn: () =>
      adminTelegramAPI.list({
        page,
        per_page: perPage,
        q: filters.q || undefined,
        branch_id: filters.branch_id || undefined,
        department_id: filters.department_id || undefined,
        role: filters.role || undefined,
        verified: filters.verified === '' ? undefined : filters.verified,
      }),
  });

  const { items: rows, meta } = extractPaginatedData<LinkRow>(listData, page, perPage);

  const { data: branchesData } = useQuery({
    queryKey: ['branches-all'],
    queryFn: () => branchesAPI.list(),
  });
  const { data: departmentsData } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsAPI.list(),
  });

  const branches = useMemo(() => {
    const raw = branchesData?.data?.data ?? branchesData?.data ?? [];
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  }, [branchesData]);

  const departments = useMemo(() => {
    const raw = departmentsData?.data?.data ?? departmentsData?.data ?? [];
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
  }, [departmentsData]);

  const sendTestMut = useMutation({
    mutationFn: (id: number) => adminTelegramAPI.sendTest(id),
    onSuccess: () => toast.success('Тестовое сообщение отправлено'),
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Не удалось отправить сообщение'),
  });

  const unlinkMut = useMutation({
    mutationFn: (id: number) => adminTelegramAPI.unlink(id),
    onSuccess: () => {
      toast.success('Подключение удалено');
      qc.invalidateQueries({ queryKey: ['admin-telegram-links'] });
      refetchStats();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Не удалось удалить подключение'),
  });

  const handleUnlink = (row: LinkRow) => {
    const name = row.employee?.full_name ?? row.user?.name ?? '#' + row.id;
    if (!confirm(`Удалить Telegram-подключение для ${name}?`)) return;
    unlinkMut.mutate(row.id);
  };

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !rows.some((r) => r.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...rows.map((r) => r.id)])));
    }
  };
  const toggleOne = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const updateFilter = (key: keyof typeof filters, value: any) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({ q: '', branch_id: '', department_id: '', role: '', verified: '' });
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-blue-600" />
            Telegram-бот — управление
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Список подключённых сотрудников, тестовые сообщения и массовые рассылки.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { refetch(); refetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-1" /> Обновить
          </Button>
          <Button onClick={() => setBroadcastOpen(true)}>
            <Send className="h-4 w-4 mr-1" /> Рассылка
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Всего подключений" value={stats.total_links} />
          <StatCard label="Подтверждены" value={stats.verified_links} color="text-green-700" />
          <StatCard label="Ожидают" value={stats.pending_links} color="text-amber-700" />
          <StatCard label="Активные сотрудники" value={stats.total_active_employees} />
          <StatCard label="Подключены" value={stats.linked_employees} color="text-blue-700" />
          <StatCard label="Не подключены" value={stats.unlinked_employees} color="text-rose-700" />
          <StatCard label="Активны за 24 ч" value={stats.active_last_24h} color="text-indigo-700" />
        </div>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Имя, username, email..."
                className="pl-8"
                value={filters.q}
                onChange={(e) => updateFilter('q', e.target.value)}
              />
            </div>
            <BranchFilterSelect
              alwaysShow
              className="border rounded px-2 py-2 text-sm"
              value={String(filters.branch_id)}
              onChange={(value) => updateFilter('branch_id', value ? Number(value) : '')}
            />
            <select
              className="border rounded px-2 py-2 text-sm"
              value={filters.department_id}
              onChange={(e) => updateFilter('department_id', e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Все отделы</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              className="border rounded px-2 py-2 text-sm"
              value={filters.verified === '' ? '' : (filters.verified ? '1' : '0')}
              onChange={(e) =>
                updateFilter('verified', e.target.value === '' ? '' : e.target.value === '1')
              }
            >
              <option value="">Все статусы</option>
              <option value="1">Подтверждены</option>
              <option value="0">Ожидают</option>
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border rounded px-2 py-1 text-sm"
              value={filters.role}
              onChange={(e) => updateFilter('role', e.target.value)}
            >
              <option value="">Все роли</option>
              <option value="super_admin">super_admin</option>
              <option value="hr_admin">hr_admin</option>
              <option value="accountant">accountant</option>
              <option value="branch_manager">branch_manager</option>
              <option value="rop_manager">rop_manager</option>
              <option value="viewer">viewer</option>
            </select>
            <Button variant="outline" onClick={clearFilters}>Очистить</Button>
            {selectedIds.length > 0 && (
              <span className="ml-auto text-sm text-slate-600">
                Выбрано: <strong>{selectedIds.length}</strong>
                <button
                  className="ml-2 text-blue-600 hover:underline"
                  onClick={() => setSelectedIds([])}
                >
                  Снять выбор
                </button>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-8"><LoadingSpinner /></div>
          ) : rows.length === 0 ? (
            <div className="p-8">
              <EmptyState title="Ничего не найдено" description="Измените фильтры или попробуйте позже." />
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th className="px-3 py-2 text-left">Telegram</th>
                  <th className="px-3 py-2 text-left">Сотрудник</th>
                  <th className="px-3 py-2 text-left">Филиал / Отдел</th>
                  <th className="px-3 py-2 text-left">Роль</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">Последняя активность</th>
                  <th className="px-3 py-2 text-right">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleOne(row.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {row.telegram_username ? '@' + row.telegram_username : '—'}
                      </div>
                      <div className="text-xs text-slate-500">{row.telegram_user_id ?? row.telegram_id ?? ''}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.employee?.full_name ?? row.user?.name ?? '—'}</div>
                      <div className="text-xs text-slate-500">
                        {row.employee?.employee_id ?? row.user?.email ?? ''}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div>{row.employee?.branch?.name ?? '—'}</div>
                      <div className="text-xs text-slate-500">{row.employee?.department?.name ?? ''}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {(row.user?.roles ?? []).map((r) => r.name).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2">
                      {row.verified_at ? (
                        <Badge variant="success" className="bg-green-100 text-green-800">Подтверждено</Badge>
                      ) : (
                        <Badge variant="warning" className="bg-amber-100 text-amber-800">Ожидает</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {formatDateTime(row.last_activity_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <span title="Отправить тестовое сообщение">
                          <Button
                            variant="outline"
                            onClick={() => sendTestMut.mutate(row.id)}
                            disabled={sendTestMut.isPending || !row.verified_at}
                          >
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                        <span title="Удалить подключение">
                          <Button
                            variant="outline"
                            onClick={() => handleUnlink(row)}
                            disabled={unlinkMut.isPending}
                            className="text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
        {meta && meta.lastPage > 1 && (
          <div className="p-3 border-t">
            <Pagination meta={meta} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <BroadcastModal
        isOpen={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        selectedIds={selectedIds}
        branches={branches}
        departments={departments}
        onSent={() => {
          setSelectedIds([]);
          setBroadcastOpen(false);
        }}
      />
    </div>
  );
}

const StatCard: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color = 'text-slate-900' }) => (
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-2xl font-semibold ${color}`}>{value ?? 0}</div>
  </div>
);
